import React, { useState } from 'react';
import { Alert, Button, Input, Link } from '@heroui/react';
import LoginApi from './LoginApi.js';
import { parseLoginError } from './parseLoginError.js';
import SocialButtons from '../auth-shared/SocialButtons.js';
import { getEnabledProviders, readOAuthClientIds } from '../auth-shared/enabledProviders.js';
import { DASHBOARD_URL, landAfterAuth } from '../auth-shared/completeOAuthSignIn.js';
import { useSocialSignIn } from '../auth-shared/useSocialSignIn.js';

/**
 * LoginForm — the sign-in form itself (password + OAuth), rendered by the
 * standalone `/authorization/` page and embedded beside registration on the
 * plan-purchase page.
 *
 * A successful login lands through `landAfterAuth`, whose profile probe sends
 * an unfinished registration to finish it before `redirectTo`.
 *
 * @param {{redirectTo?: string}} props Where to send the visitor once
 *   authenticated and not gated. Defaults to the dashboard.
 */
export default function LoginForm({ redirectTo = DASHBOARD_URL } = {}) {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const [resend, setResend] = useState({ status: 'idle', message: null });
	const clientIds = readOAuthClientIds();
	const providers = getEnabledProviders(clientIds);
	const social = useSocialSignIn({
		clientIds,
		redirectTo,
		onStart: () => setError(null),
		onError: err =>
			// A client-side failure (closed or blocked popup) has no backend response for parseLoginError.
			setError(
				err?.response
					? parseLoginError(err)
					: { kind: 'generic', message: err?.message || 'Sign-in failed. Please try again.' }
			)
	});
	// 'microsoft' locks nothing: its popup cannot be watched, so the visitor may retry or switch at any time.
	const isBusy = submitting || social.phase === 'google' || social.phase === 'exchanging';
	const isLoading = submitting || social.phase === 'exchanging';

	const handleSubmit = async event => {
		event.preventDefault();
		if (social.isExchanging()) return;
		social.cancelMicrosoftWait();
		setError(null);
		setSubmitting(true);
		try {
			const { data } = await LoginApi.login(email, password);
			await landAfterAuth(data?.data?.accessToken, redirectTo);
		} catch (err) {
			setError(parseLoginError(err));
			setSubmitting(false);
		}
	};

	const handleResend = async () => {
		setResend({ status: 'sending', message: null });
		try {
			const { data } = await LoginApi.resendConfirmation(error?.email);
			setResend({ status: 'sent', message: data?.message });
		} catch (err) {
			setResend({ status: 'failed', message: parseLoginError(err).message });
		}
	};

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-5">
			{error && (
				<Alert color="danger" variant="flat" radius="sm">
					<div className="flex flex-col items-start gap-1">
						<span>{error.message}</span>
						{/* Only an unconfirmed account can act on this — every other
						    failure has nothing to resend. */}
						{error.kind === 'not_activated' && (
							<Link
								as="button"
								type="button"
								size="sm"
								className="text-primary"
								isDisabled={resend.status === 'sending'}
								onPress={handleResend}
							>
								{resend.status === 'sending' ? 'Sending…' : 'Resend confirmation email'}
							</Link>
						)}
					</div>
				</Alert>
			)}

			{resend.status === 'sent' && (
				<Alert color="success" variant="flat" radius="sm">
					{resend.message}
				</Alert>
			)}
			{resend.status === 'failed' && (
				<Alert color="danger" variant="flat" radius="sm">
					{resend.message}
				</Alert>
			)}

			<div className="flex flex-col gap-4">
				<Input
					name="email"
					type="email"
					label="Email"
					labelPlacement="outside-top"
					variant="bordered"
					radius="sm"
					autoComplete="username"
					maxLength={50}
					isRequired
					value={email}
					onValueChange={setEmail}
					classNames={{ inputWrapper: 'h-11' }}
				/>
				<Input
					name="password"
					type="password"
					label="Password"
					labelPlacement="outside-top"
					variant="bordered"
					radius="sm"
					autoComplete="current-password"
					maxLength={50}
					isRequired
					value={password}
					onValueChange={setPassword}
					classNames={{ inputWrapper: 'h-11' }}
				/>
			</div>

			<Button
				type="submit"
				color="primary"
				radius="sm"
				className="h-11 font-medium"
				isLoading={isLoading}
				isDisabled={isBusy}
			>
				{isLoading ? 'Signing in…' : 'Sign In'}
			</Button>

			<SocialButtons
				providers={providers}
				onGoogle={social.signInGoogle}
				onMicrosoft={social.signInMicrosoft}
				isGoogleDisabled={submitting || social.phase === 'exchanging'}
				isMicrosoftDisabled={isBusy}
				showMicrosoftHint={social.phase === 'microsoft'}
			/>
		</form>
	);
}
